import {
  AnalyticsReportJob,
  getMainAnalyticsReportFromServer,
  getMainAnalyticsReportStatusFromServer,
  MainAnalyticsReportRequest,
} from "./reportsApi";

export const ANALYTICS_REPORT_POLL_INTERVAL_MS = 4000;
export const ANALYTICS_REPORT_POLL_TIMEOUT_MS = 15 * 60 * 1000;
const POLL_CANCEL_SLICE_MS = 250;

export type AnalyticsReportUiState = {
  url?: string;
  noDataFound: boolean;
  isSentToEmail: boolean;
  error?: string;
};

type WaitOptions = {
  pollIntervalMs?: number;
  timeoutMs?: number;
  isCancelled?: () => boolean;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  onUpdate?: (job: AnalyticsReportJob) => void;
  start?: (request: MainAnalyticsReportRequest) => Promise<AnalyticsReportJob>;
  status?: (request: MainAnalyticsReportRequest) => Promise<AnalyticsReportJob>;
};

/** Polls a pending analytics download until it settles.
 *  A response with no status is already final.
 *  See ./AGENTS.md → "Analytics report download".
 */
export async function waitForMainAnalyticsReport(
  request: MainAnalyticsReportRequest,
  options: WaitOptions = {}
): Promise<AnalyticsReportJob> {
  const pollIntervalMs =
    options.pollIntervalMs ?? ANALYTICS_REPORT_POLL_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? ANALYTICS_REPORT_POLL_TIMEOUT_MS;
  const isCancelled = options.isCancelled ?? (() => false);
  const sleep = options.sleep ?? defaultSleep;
  const now = options.now ?? Date.now;
  const start = options.start ?? getMainAnalyticsReportFromServer;
  const status = options.status ?? getMainAnalyticsReportStatusFromServer;
  const deadline = now() + timeoutMs;

  let job = await start(request);
  const emailed = Boolean(job.isEmailed);
  options.onUpdate?.(job);
  if (isCancelled() || isAnalyticsReportSettled(job)) return job;

  while (now() < deadline) {
    const keepGoing = await pause(
      Math.min(pollIntervalMs, Math.max(deadline - now(), 0)),
      isCancelled,
      sleep
    );
    if (!keepGoing) return job;
    job = await status(request);
    // Status responses omit isEmailed, so keep the flag from the start call.
    if (emailed) job = { ...job, isEmailed: true };
    options.onUpdate?.(job);
    if (isCancelled() || isAnalyticsReportSettled(job)) return job;
  }

  return { ...job, status: "failed", message: "timed out" };
}

export function analyticsReportUiState(
  job: AnalyticsReportJob | null | undefined
): AnalyticsReportUiState {
  if (!job) {
    return {
      noDataFound: false,
      isSentToEmail: false,
      error: "The report could not be created. Try again.",
    };
  }
  if (job.isEmpty || job.message === "empty") {
    return {
      noDataFound: true,
      isSentToEmail: Boolean(job.isEmailed),
    };
  }
  if (job.urlLink) {
    return {
      url: job.urlLink,
      noDataFound: false,
      isSentToEmail: Boolean(job.isEmailed),
    };
  }
  if (job.isEmailed && job.status !== "failed" && job.status !== "missing") {
    return { noDataFound: false, isSentToEmail: true };
  }
  if (job.status === "pending") {
    return {
      noDataFound: false,
      isSentToEmail: Boolean(job.isEmailed),
    };
  }
  return {
    noDataFound: false,
    isSentToEmail: Boolean(job.isEmailed),
    error: reportFailureCopy(job.message),
  };
}

function isAnalyticsReportSettled(job: AnalyticsReportJob): boolean {
  if (job.urlLink || job.isEmpty || job.message === "empty") return true;
  return job.status !== "pending";
}

function reportFailureCopy(message?: string): string {
  if (message === "timed out") {
    return "The report is still running. Try again in a few minutes.";
  }
  return "The report could not be created. Try again.";
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pause(
  ms: number,
  isCancelled: () => boolean,
  sleep: (ms: number) => Promise<void>
): Promise<boolean> {
  let left = ms;
  while (left > 0) {
    if (isCancelled()) return false;
    await sleep(Math.min(POLL_CANCEL_SLICE_MS, left));
    left -= POLL_CANCEL_SLICE_MS;
  }
  return !isCancelled();
}
